import type { Artifact } from "../../../../stores/artifact";
import type { DockPanel } from "../../../../stores/dock";

/**
 * The artifact kind's payload + the two conversions between an `Artifact` and
 * the `DockPanel` carrying it.
 *
 * This lives apart from `index.tsx` (which builds the kind definition) on
 * purpose: the panel and toolbar components need these helpers, and the kind
 * definition needs those components. Putting both in one module makes a real
 * import cycle — survivable via hoisting, but only by accident, and the first
 * person to convert a function to a const would find out the hard way.
 */

/** What a `kind: "artifact"` panel keeps in its opaque `data`. The dock host
 *  never reads this — only the artifact kind's own components do. */
export interface ArtifactData {
	language: string;
	content: string;
}

/** Read an artifact panel's payload. Total by construction: `data` is typed
 *  `unknown` on DockPanel (that opacity is what keeps the host kind-blind), so
 *  somebody has to narrow it, and it should be exactly one place. */
export function artifactData(panel: DockPanel): ArtifactData {
	const d = panel.data as Partial<ArtifactData> | undefined;
	return { language: d?.language ?? "", content: d?.content ?? "" };
}

/** Build the dock panel for an artifact. The id is the artifact's content hash,
 *  which is what makes `openPanel` idempotent — auto-open and a manual Canvas
 *  click on the same block land on one tab, not two. */
export function artifactPanel(a: Artifact): DockPanel {
	return {
		id: a.id,
		kind: "artifact",
		title: a.title,
		data: { language: a.language, content: a.content } satisfies ArtifactData,
		// Chat is the only producer of these panels; module-scoping keeps a
		// chat artifact out of, say, the media page's dock (owner rule).
		scope: "chat",
	};
}
